import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { ButtonTranslucent } from "@nova/component-btn"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { zIndex } from "@nova/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectWeatherEnabled, selectWeatherPanel } from "../control/selectors"
import { setWeather } from "../control/slice"
import { WeatherMode } from "../control/types"
import { SelectWeatherMode } from "../layers/weather/controls/SelectWeatherMode"

const MODE_META: Record<WeatherMode, { title: string; Icon: Icons.IconComponent }> = {
    wind: { title: "Wind", Icon: Icons.Wind },
    precipitation: { title: "Precipitation", Icon: Icons.Precipitation },
    radar: { title: "Radar", Icon: Icons.Radar },
    lightning: { title: "Lightning", Icon: Icons.Lightning },
}

type Props = { isLight: boolean; isOpen: boolean; onOpenChange: (open: boolean) => void }

export const WeatherLayersFooterButton = ({ isLight, isOpen, onOpenChange }: Props) => {
    const dispatch = useAppDispatch()
    const enabled = useAppSelector(selectWeatherEnabled)
    const { mode } = useAppSelector(selectWeatherPanel)

    if (!enabled) {
        return (
            <div className="relative">
                <ButtonTranslucent
                    Icon={Icons.Weather}
                    title="Weather"
                    isActive={isOpen}
                    isLight={isLight}
                    onClick={() => onOpenChange(!isOpen)}
                />
                {isOpen && (
                    <ClickAwayListener onClickAway={() => onOpenChange(false)}>
                        <div
                            className="absolute w-[240px] rounded-xl bg-gray-900 px-2 py-4 shadow-card"
                            style={{
                                bottom: "calc(100% + 0.5rem)",
                                zIndex: zIndex.MapLayerManager,
                            }}
                        >
                            <div className="mb-3 flex items-center justify-between px-2">
                                <h5 className="text-gray-0">Weather</h5>
                                <Icons.SmallIcon
                                    Icon={Icons.XClose}
                                    className="text-gray-100 hover:text-gray-0 cursor-pointer"
                                    onClick={() => onOpenChange(false)}
                                />
                            </div>
                            <SelectWeatherMode
                                closeOnSelection
                                onClose={() => onOpenChange(false)}
                                onModeSelect={() => dispatch(setWeather(true))}
                            />
                        </div>
                    </ClickAwayListener>
                )}
            </div>
        )
    }

    const { title, Icon } = MODE_META[mode]

    return (
        <button
            type="button"
            aria-label="Turn off weather layer"
            onClick={() => dispatch(setWeather(false))}
            className={classNames(
                "group h-9 flex items-center gap-1.5 border border-blue-500 rounded-[1.125rem] px-3 cursor-pointer",
                isLight
                    ? "bg-white-500 text-gray-900 hover:bg-white-1000"
                    : "bg-black-600 text-gray-0 hover:bg-gray-900"
            )}
        >
            <Icons.SmallIcon Icon={Icon} />
            <small>{title}</small>
            <Icons.SmallIcon
                Icon={Icons.XClose}
                className={classNames(
                    "ml-0.5",
                    isLight
                        ? "text-black-600 group-hover:text-gray-900"
                        : "text-white-800 group-hover:text-gray-0"
                )}
            />
        </button>
    )
}
